import logger from './logger';

type SortableValue = string | number | Date | unknown[];

type SortableObject = Record<string, unknown>;

const normalize = (value: unknown): SortableValue => {
  if (Array.isArray(value)) {
    return value.length;
  }

  if (typeof value === 'number' || value instanceof Date) {
    return value;
  }

  return value && typeof value === 'string'
    ? value.toLowerCase()
    : (value as SortableValue);
};

const propertyExists = (value: unknown): boolean =>
  !!value || value === 0 || value === '';

function descendingComparator<T extends SortableObject>(
  a: T,
  b: T,
  orderBy: keyof T
): number {
  const normalizedA = normalize(a[orderBy]);
  const normalizedB = normalize(b[orderBy]);
  if (propertyExists(normalizedA) || propertyExists(normalizedB)) {
    if (normalizedB < normalizedA || !propertyExists(normalizedB)) {
      return -1;
    }
    if (normalizedB > normalizedA || !propertyExists(normalizedA)) {
      return 1;
    }
    return 0;
  }
  logger.debug(
    `Unable to sort, most probably property '${String(orderBy)}' is not present in object`,
    a,
    b
  );
  return 0;
}

export function getComparator<T extends SortableObject>(
  order: 'asc' | 'desc',
  orderBy: keyof T
): (a: T, b: T) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface ItemWithId {
  id: string | number;
}

export function itemIsIncluded<T extends ItemWithId>(
  itemId: string | number,
  itemsArray: T[]
): boolean {
  return itemsArray.findIndex((item) => item.id === itemId) !== -1;
}
